import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEvidence } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

const evidenceSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  type: z.enum(['file', 'url', 'note']),
  url: z.string().optional(),
  note: z.string().optional(),
  file: z.any().optional(),
});

type EvidenceFormData = z.infer<typeof evidenceSchema>;

interface EvidenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  relatedType: string;
}

export function EvidenceDialog({ open, onOpenChange, nodeId, relatedType }: EvidenceDialogProps) {
  const { organization } = useAuth();
  const { createEvidence } = useEvidence(organization?.id);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EvidenceFormData>({
    resolver: zodResolver(evidenceSchema),
    defaultValues: {
      type: 'note',
    },
  });

  const watchedType = watch('type');

  const onSubmit = async (data: EvidenceFormData) => {
    try {
      setUploading(true);
      let fileUrl = data.url;

      if (data.type === 'file' && selectedFile) {
        const filePath = `${nodeId}/${Date.now()}_${selectedFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('evidence')
          .upload(filePath, selectedFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('evidence')
          .getPublicUrl(filePath);
        
        fileUrl = urlData.publicUrl;
      }

      await createEvidence({
        name: data.title,
        type: data.type,
        storage_path: fileUrl,
        notes: data.note,
        related_to_type: relatedType,
        related_to_id: nodeId,
        org_id: organization!.id,
      });
      
      reset();
      setSelectedFile(null);
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding evidence:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Evidence</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter evidence title"
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="type">Type *</Label>
            <Select
              value={watchedType}
              onValueChange={(value) => setValue('type', value as 'file' | 'url' | 'note')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="url">URL/Link</SelectItem>
                <SelectItem value="file">File (placeholder)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {watchedType === 'file' && (
            <div>
              <Label htmlFor="file">Upload File</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              />
            </div>
          )}

          {watchedType === 'url' && (
            <div>
              <Label htmlFor="url">URL</Label>
              <Input
                id="url"
                type="url"
                {...register('url')}
                placeholder="https://example.com/document"
              />
            </div>
          )}

          {(watchedType === 'note' || watchedType === 'file') && (
            <div>
              <Label htmlFor="note">
                {watchedType === 'file' ? 'File Description' : 'Note'}
              </Label>
              <Textarea
                id="note"
                {...register('note')}
                placeholder={
                  watchedType === 'file' 
                    ? "Describe the file and its contents"
                    : "Enter your note"
                }
                className="min-h-[100px]"
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Evidence'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}